import React, { useState, useRef } from 'react';
import { Button, Input } from 'reactstrap';
import Modal from './Modal';

const PdfSectionModal = ({ isOpen, onClose, configId, sections, onSave }) => {
  const [sectionNames, setSectionNames] = useState(sections || []);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const sectionContainerRef = useRef(null);
  const lastSectionRef = useRef(null);

  const handleSectionNameChange = (index, newName) => {
    const updatedSectionNames = [...sectionNames];
    updatedSectionNames[index] = newName;
    setSectionNames(updatedSectionNames);
  };

  const addSection = () => {
    setSectionNames((prevSections) => [...prevSections, '']);
    setTimeout(() => {
      lastSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  const deleteSection = (index) => {
    const updatedSectionNames = sectionNames.filter((_, i) => i !== index);
    setSectionNames(updatedSectionNames);
  };

  const handleSave = async () => {
    const trimmedSectionNames = sectionNames
      .map((name) => name.trim())
      .filter((name) => name);
    await onSave(configId, trimmedSectionNames);
    onClose();
  };

  const handleDragStart = (index) => setDraggingIndex(index);

  const handleDrop = (index) => {
    if (draggingIndex !== null && draggingIndex !== index) {
      const updatedSectionNames = [...sectionNames];
      const [movedSection] = updatedSectionNames.splice(draggingIndex, 1);
      updatedSectionNames.splice(index, 0, movedSection);
      setSectionNames(updatedSectionNames);
    }
    setDraggingIndex(null);
  };

  const handleDragOver = (e) => e.preventDefault();

  const renderSectionNames = () =>
    sectionNames.map((name, index) => (
      <div
        key={index}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDrop={() => handleDrop(index)}
        onDragOver={handleDragOver}
        style={{
          marginBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: draggingIndex === index ? '#e3f7ff' : '#f7f7f7',
          borderRadius: '8px',
          padding: '10px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
          transform: draggingIndex === index ? 'scale(1.02)' : 'scale(1)',
          transition: 'transform 0.2s ease-in-out',
        }}
        ref={index === sectionNames.length - 1 ? lastSectionRef : null}
      >
        <Button
          style={{
            minWidth: '30px',
            backgroundColor: 'transparent',
            cursor: 'grab',
          }}
        >
          <i className="fa fa-bars" style={{ color: '#888' }} />
        </Button>
        <Input
          id={`section-${index}`}
          type="text"
          value={name}
          onChange={(e) => handleSectionNameChange(index, e.target.value)}
          placeholder={`Section ${index + 1}`}
          style={{
            flexGrow: 1,
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            marginRight: '8px',
          }}
        />
        <Button
          onClick={() => deleteSection(index)}
          style={{
            minWidth: '30px',
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
        >
          <i
            className="fa fa-trash"
            style={{
              fontSize: '16px',
              color: '#888',
              transition: 'color 0.3s',
            }}
            onMouseEnter={(e) => (e.target.style.color = '#f76c6c')}
            onMouseLeave={(e) => (e.target.style.color = '#888')}
          />
        </Button>
      </div>
    ));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      buttons={[
        {
          label: 'Looks Good! Continue',
          color: 'primary',
          onClick: handleSave,
        },
      ]}
    >
      <div style={{ textAlign: 'center', margin: '8px 0' }}>
        {/* Step Tracker */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '14px',
              width: '80%',
              height: '2px',
              backgroundColor: '#ccc',
              zIndex: 0,
            }}
          />
          {[
            'Section Extraction',
            'Content Extraction',
            'Instructions Generation',
          ].map((step, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 1,
                animation: `fadeInSlide 0.5s ${idx * 0.15}s ease forwards`,
                opacity: 0,
                margin: '0 10px', // Spacing between steps
                cursor: idx === 1 ? 'pointer' : 'default',
              }}
              onClick={idx === 1 ? handleSave : () => {}}
            >
              <div
                style={{
                  backgroundColor: idx === 0 ? '#6bd888' : '#6c757d', // Different colors for each step
                  color: '#fff',
                  width: '32px', // Slightly larger circle
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '500',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow for better contrast
                  transition: 'transform 0.2s ease, background-color 0.3s ease',
                  transform: idx === 0 ? 'scale(1.4)' : 'scale(1)', // Slight scaling for active step
                }}
              >
                {idx + 1}
              </div>
              <div
                style={{
                  fontSize: '13px', // Slightly larger font for better readability
                  marginTop: '5px',
                  color: idx === 0 ? '#6bd888' : '#333', // Active step text color
                }}
              >
                {step}
              </div>
            </div>
          ))}
        </div>

        {/* Header */}
        <h2
          style={{
            fontWeight: '500',
            marginBottom: '4px',
            fontSize: '18px',
            color: '#333',
          }}
        ></h2>
        <p
          style={{
            fontWeight: '900',
            fontSize: '15px',
            color: '#666',
            margin: '0 auto',
            backgroundColor: '#FFF3CD',
          }}
        >
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
            style={{ color: '#f76c6c', marginRight: '4px' }}
          />
          Review section names and confirm their order to guide accurate text
          extraction and generate your custom template.
        </p>
      </div>

      {/* Add Section Button */}
      <Button
        color="primary"
        onClick={addSection}
        style={{
          display: 'block',
          margin: '12px auto',
          padding: '8px 20px',
          fontWeight: '500',
          borderRadius: '5px',
          animation: 'fadeIn 0.5s ease forwards',
        }}
      >
        <i className="fa fa-plus" style={{ marginRight: '6px' }} /> Add Section
      </Button>

      {/* Sections Container */}
      <div
        ref={sectionContainerRef}
        className="scrollable-container"
        style={{
          maxHeight: '300px',
          overflowY: 'scroll', // Keeps scrollbar visible
          padding: '10px 0',
          borderRadius: '8px',
        }}
      >
        {renderSectionNames()}
      </div>

      <style>
        {`
          .scrollable-container::-webkit-scrollbar {
            width: 5px;
          }
          .scrollable-container::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 8px;
          }
          .scrollable-container::-webkit-scrollbar-thumb:hover {
            background-color: #555;
          }
          .scrollable-container::-webkit-scrollbar-track {
            background-color: #f0f0f0;
            border-radius: 8px;
          }
      `}
      </style>

      {/* Animation Keyframes */}
      <style>
        {`
          @keyframes fadeInSlide {
            0% { opacity: 0; transform: translateY(-15px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Modal>
  );
};

export default PdfSectionModal;
